<template>
  <section class="home-block">
    <div class="container">
      <div ref="content" class="content">
        <h1 class="title h2">Контакты</h1>
        <template v-if="hasContacts">
          <div class="address">
            <div class="h5 address__title">Адрес объекта</div>
            <div class="h3 address__text">
              {{ contacts[0].addr_location }}
            </div>
          </div>
          <div class="address">
            <div class="h5 address__title">Адрес офиса продаж</div>
            <div class="h3 address__text">
              {{ contacts[1].addr_location }}
            </div>
          </div>
          <div
            class="map"
            @wheel.passive.capture="wheelHandler"
            @mousedown.passive="overlay = false"
            @touchstart.passive="touchHandler"
          >
            <yandex-map
              :key="mapKey"
              v-model="map"
              width="100%"
              height="100%"
              :settings="{
                location: {
                  center: [113.469519, 52.058269],
                  zoom: 14,
                },
              }"
              @init="onMapInit"
            >
              <yandex-map-default-scheme-layer />
              <yandex-map-default-features-layer />
              <yandex-map-controls :settings="{ position: 'right' }">
                <ZoomControls />
              </yandex-map-controls>

              <yandex-map-marker
                :settings="{
                  coordinates: officeCoords,
                  properties: {},
                }"
              >
                <div class="marker">
                  <img
                    :src="markerSrc"
                    alt="Офис продаж"
                    style="display: block; width: 100%; height: 100%"
                  />
                </div>
              </yandex-map-marker>

              <yandex-map-marker
                :settings="{
                  coordinates: zkCoords,
                  properties: {},
                }"
              >
                <div class="marker">
                  <img
                    :src="markerOfficeSrc"
                    alt="Адрес ЖК"
                    style="display: block; width: 100%; height: 100%"
                  />
                </div>
              </yandex-map-marker>
            </yandex-map>
            <div
              class="map-overlay"
              :class="{
                'is-active': overlay,
                'is-touch-active': isMapTouchActive,
              }"
            >
              <p class="map-overlay__text">
                Чтобы изменить масштаб, прокручивайте карту удерживая CTRL
              </p>
              <p class="map-overlay__text map-overlay__text_mobile">
                Чтобы активировать карту,
                <br />
                нажмите кнопку ниже
                <br />
              </p>
              <div class="particle"></div>
            </div>
            <button
              class="btn btn-default btn-small"
              @click="mobileTouchActivate"
            >
              {{
                !isMapTouchActive
                  ? "Активировать карту"
                  : "Деактивировать карту"
              }}
            </button>
            <div class="map__shade" />
          </div>

          <div class="time">
            <div class="h6 time__title">Время работы</div>
            <div
              v-if="contacts[1]?.addr_work_time"
              class="time__text"
              v-html="contacts[1].addr_work_time"
            />
          </div>

          <div class="conts row justify-content-lg-between flex-wrap">
            <div v-if="phone.number && captchaVerified.state.captchaVerified" class="conts__item col-sm-auto">
              <div class="h6 conts__title">
                <a
                  :href="'tel:' + $filters.phoneFormat(phone.number)"
                  :id="phone.class ? 'comagic_phone' : undefined"
                  :class="phone.class"
                >
                  {{ phone.number }}
                </a>
              </div>
              <p class="conts__text">Контактный телефон</p>
            </div>
          </div>

          <div class="conts row justify-content-lg-between flex-wrap">
            <div v-if="contacts[1]?.addr_email" class="conts__item col-sm-auto">
              <div class="h6 conts__title">
                <a :href="'mailto:' + contacts[1].addr_email">
                  {{ contacts[1].addr_email }}
                </a>
              </div>
              <p class="conts__text">Электронная почта</p>
            </div>
          </div>
        </template>

        <div class="time btns row no-gutters">
          <div
            v-if="contacts[0]?.pdf_drive_parking?.source"
            class="btns__item col-sm-auto"
          >
            <a
              class="btn btn-transparent btn-small"
              :href="contacts[0].pdf_drive_parking.source"
              target="_blank"
            >
              Схема проезда
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, inject, shallowRef } from "vue";
import { gsap } from "gsap";
import { is_touch } from "@/helpers.js";
import {
  YandexMap,
  YandexMapDefaultSchemeLayer,
  YandexMapDefaultFeaturesLayer,
  YandexMapMarker,
  YandexMapControls,
} from "vue-yandex-maps";
import { useRoute } from "vue-router";
import { resolvePhone } from "@/utils/phones";

import ZoomControls from "@/components/home/ZoomControls.vue";

import markerSrc from "@i/marker.svg";
import markerOfficeSrc from "@i/marker_office.png";

const captchaVerified = inject("captchaStore");
const mainStore = inject("mainStore");
const map = shallowRef(null);
const content = ref(null);
const mapKey = ref(0);
const zoom = ref(13);
const mapInstance = ref(null);
const overlay = ref(false);
let timeout = null;
const isMapTouchActive = ref(false);

const contacts = computed(
  () => mainStore.state.data?.addresses?.addr_list || []
);
const hasContacts = computed(() => contacts.value.length >= 2);

const mapSettings = computed(() => ({
  location: {
    center: officeCoords.value,
    zoom: zoom.value,
  },
}));

const officeCoords = computed(() =>
  hasContacts.value
    ? [contacts.value[1].addr_lon, contacts.value[1].addr_lat]
    : [37.593605, 55.833031]
);

const zkCoords = computed(() =>
  hasContacts.value
    ? [contacts.value[0].addr_lon, contacts.value[0].addr_lat]
    : [37.593605, 55.833031]
);

const showZoomOverlay = () => {
  overlay.value = true;

  timeout = setTimeout(() => {
    overlay.value = false;
  }, 1500);
};

const onMapInit = (instance) => {
  mapInstance.value = instance;
};

onMounted(() => {
  if (!is_touch() && content.value) {
    gsap.from(content.value.children, {
      autoAlpha: 0,
      y: 20,
      stagger: { each: 0.1 },
    });
  }
});

const wheelHandler = (e) => {
  if (e.ctrlKey) {
    overlay.value = false;
  } else {
    e.stopPropagation();
    clearTimeout(timeout);
    showZoomOverlay();
  }
};

const touchHandler = (e) => {
  if (!isMapTouchActive.value) {
    clearTimeout(timeout);
    showZoomOverlay();
  }
};

const mobileTouchActivate = () => {
  isMapTouchActive.value = !isMapTouchActive.value;
};

const route = useRoute();
const phone = computed(() =>
  resolvePhone(route.name, contacts.value?.[1]?.addr_phone || "")
);
</script>

<style scoped>
.content {
  max-width: 480px;
}

.content > *:not(.map) {
  position: relative;
  z-index: 4;
}

.h2 {
  margin-bottom: 48px;
  color: #545863;
  font-weight: 700;
  font-size: 120px;
  line-height: 110%;
}

.h3 {
  color: #291f1f;
  font-weight: 400;
  font-size: 33px;
}

.h5 {
  color: #ff0020;
  text-transform: none;
}

.address {
  margin-bottom: 40px;
}

.address__title {
  margin: 0 0 8px;
}

.address__text {
  margin: 0;
  line-height: 0.9;
}

.conts__item {
  margin-bottom: 20px;
}

.conts__title {
  margin: 0;
  font-weight: 500;
}

.conts__title a {
  color: inherit;
  text-decoration: none;
}

body.-notouch .conts__title a:hover {
  color: #ff0020;
}

.conts__text {
  margin: 5px 0 0;
  font-weight: 400;
}

.time {
  margin-bottom: 25px;
}

.time__title {
  margin: 0;
  color: #291f1f;
  font-weight: 500;
}

.time__text {
  margin: 10px 0 0;
  color: #291f1f;
  font-weight: 400;
}

.time__text :deep(h6),
.time__text :deep(.h6) {
  margin: 10px 0;
  color: inherit;
  font-weight: 500;
  font-size: inherit;
  text-transform: none;
}

.btns__item {
  margin: 0 3px 0 0;
}

.map {
  position: absolute;
  top: 76px;
  right: 0;
  width: 62%;
  height: 630px;
  min-height: 400px;
}

.map::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 27%;
  height: 100%;
  background: linear-gradient(-90deg, rgb(255 255 255 / 0) 70%, #fff 100%);
  pointer-events: none;
}

.map::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgb(255 255 255 / 0) 70%, #fff 100%);
  pointer-events: none;
}

.map__shade::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  width: 27%;
  height: 100%;
  background: linear-gradient(90deg, rgb(255 255 255 / 0) 70%, #fff 85%);
  pointer-events: none;
}

.map__shade::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 27%;
  background: linear-gradient(0deg, rgb(255 255 255 / 0) 70%, #fff 85%);
  pointer-events: none;
}

.marker {
  position: relative;
  width: 54px;
  height: 54px;
  transform: translate(-50%, -100%);
}

.contacts-page__mob {
  display: none;
}

.map-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 40px;
  background: #ff002066;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.45s ease-in-out,
    -webkit-backdrop-filter 0.45s ease-in-out;
  transition: opacity 0.45s ease-in-out, backdrop-filter 0.45s ease-in-out;
  transition: opacity 0.45s ease-in-out, backdrop-filter 0.45s ease-in-out,
    -webkit-backdrop-filter 0.45s ease-in-out;
  backdrop-filter: blur(10px);
}

.map-overlay.is-active {
  opacity: 1;
  backdrop-filter: blur(7px);
}

.map-overlay.is-touch-active {
  pointer-events: none;
}

.map-overlay__text {
  width: 50%;
  color: #fff;
  font-weight: 600;
  font-size: 40px;
  text-align: center;
}

.map-overlay__text.map-overlay__text_mobile {
  display: none;
}

.btn {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  display: none;
  margin: 10px 34px;
  font-size: 14px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.particle {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  background: url(~@i/logo-map-root.svg) 95% center / contain no-repeat;
  pointer-events: none;
}

:deep(.ymaps3x0--controls_right) {
  right: 60px;
}

@media (max-width: 1279px) {
  .map {
    width: 60%;
  }
}

@media (max-width: 991px) {
  .content {
    display: flex;
    flex-direction: column;
    max-width: none;
  }

  .h2 {
    font-size: 48px;
  }

  .address {
    margin-bottom: 20px;
  }

  .map {
    position: relative;
    top: 40px;
    order: 1;
    width: auto;
    height: 450px;
    margin: -50px 0 20px;
  }

  .map__shade {
    display: block;
  }

  .map::before {
    width: 40px;
  }

  .map::after {
    height: 160px;
  }

  .map__shade::before {
    width: 40px;
  }

  .map__shade::after {
    height: 160px;
  }

  .conts {
    margin-bottom: 5px;
  }

  .time {
    margin-bottom: 35px;
  }

  :deep(.ymaps3x0--controls_right) {
    right: 10px !important;
  }
}

@media (max-width: 767px) {
  .map-overlay {
    pointer-events: auto;
  }

  .map-overlay__text {
    display: none;
  }

  .map-overlay__text.map-overlay__text_mobile {
    display: block;
    width: 45%;
    font-size: 20px;
    text-align: center;
  }

  .btn {
    display: block;
  }

  .particle {
    background-position: 120px;
    background-size: auto 100%;
  }
}

@media (max-width: 575px) {
  .map {
    height: 400px;
    margin-right: -15px;
    margin-bottom: 40px;
    margin-left: -15px;
  }

  .map::before {
    display: none;
  }

  .map__shade::before {
    display: none;
  }

  .conts {
    margin-bottom: 0;
  }

  .btns {
    margin-bottom: -10px;
    text-align: center;
  }

  .btns__item {
    margin-bottom: 24px;
  }

  .contacts-page__mob {
    display: block;
  }

  .map-overlay__text.map-overlay__text_mobile {
    width: 80%;
  }
}
</style>
